import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

const normalizeText = (value) => String(value ?? '').toLowerCase().trim();

const sanitizeFileName = (value) => (
  String(value || 'grilla')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'grilla'
);

const getCellValue = (row, column, mode = 'display') => {
  if (mode === 'export' && typeof column.exportValue === 'function') {
    return column.exportValue(row);
  }
  if (mode === 'search' && typeof column.searchValue === 'function') {
    return column.searchValue(row);
  }
  if (typeof column.value === 'function') {
    return column.value(row);
  }
  if (column.key) {
    return row?.[column.key];
  }
  return '';
};

const DataGrid = ({
  title = 'Grilla',
  rows = [],
  columns = [],
  rowKey,
  actions,
  actionsHeader = 'Acciones',
  emptyText = 'Sin datos',
  searchPlaceholder = 'Buscar',
  exportFileName,
  defaultPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  enableSearch = true,
  enableExport = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);

  const availablePageSizeOptions = useMemo(
    () => Array.from(new Set([...pageSizeOptions, defaultPageSize])).sort((a, b) => a - b),
    [pageSizeOptions, defaultPageSize]
  );
  const searchableColumns = useMemo(
    () => columns.filter((column) => column.searchable !== false),
    [columns]
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(searchTerm);
    if (!term) return rows;

    return rows.filter((row) => (
      searchableColumns.some((column) => normalizeText(getCellValue(row, column, 'search')).includes(term))
    ));
  }, [rows, searchableColumns, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pagedRows = filteredRows.slice(startIndex, startIndex + pageSize);
  const visibleFrom = filteredRows.length === 0 ? 0 : startIndex + 1;
  const visibleTo = Math.min(startIndex + pageSize, filteredRows.length);
  const exportName = sanitizeFileName(exportFileName || title);

  const exportRows = () => filteredRows.map((row) => (
    columns.reduce((acc, column) => {
      acc[column.header] = getCellValue(row, column, 'export');
      return acc;
    }, {})
  ));

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(exportRows());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
    XLSX.writeFile(workbook, `${exportName}.xlsx`);
  };

  const handleExportPdf = () => {
    const doc = new jsPDF({
      orientation: columns.length > 5 ? 'landscape' : 'portrait',
      unit: 'pt',
      format: 'a4'
    });
    doc.setFontSize(14);
    doc.text(title, 40, 40);
    autoTable(doc, {
      startY: 58,
      head: [columns.map((column) => column.header)],
      body: filteredRows.map((row) => columns.map((column) => getCellValue(row, column, 'export'))),
      styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak' },
      headStyles: { fillColor: [11, 114, 182], textColor: 255, fontStyle: 'bold' },
      margin: { left: 40, right: 40 }
    });
    doc.save(`${exportName}.pdf`);
  };

  return (
    <div className="ctacte-data-grid">
      <div className="ctacte-grid-toolbar">
        {enableSearch && (
          <label className="ctacte-grid-search">
            <i className="fas fa-search" />
            <input
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
            />
          </label>
        )}

        <div className="ctacte-grid-controls">
          <label className="ctacte-grid-page-size">
            <span>Ver</span>
            <select value={pageSize} onChange={handlePageSizeChange}>
              {availablePageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>

          {enableExport && (
            <div className="ctacte-grid-export">
              <button type="button" className="export-excel" onClick={handleExportExcel} disabled={filteredRows.length === 0} title="Exportar Excel">
                <i className="fas fa-file-excel" />
                <span>Excel</span>
              </button>
              <button type="button" className="export-pdf" onClick={handleExportPdf} disabled={filteredRows.length === 0} title="Exportar PDF">
                <i className="fas fa-file-pdf" />
                <span>PDF</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="ctacte-table-wrap">
        <table className="ctacte-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key || column.header}>{column.header}</th>
              ))}
              {actions && <th>{actionsHeader}</th>}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, index) => (
              <tr key={rowKey ? rowKey(row) : index}>
                {columns.map((column) => (
                  <td key={column.key || column.header} className={column.className || ''}>
                    {typeof column.render === 'function'
                      ? column.render(row)
                      : getCellValue(row, column)}
                  </td>
                ))}
                {actions && <td className="ctacte-row-actions">{actions(row)}</td>}
              </tr>
            ))}
            {pagedRows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="ctacte-empty">{emptyText}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="ctacte-grid-footer">
        <span>{visibleFrom}-{visibleTo} de {filteredRows.length}</span>
        <div className="ctacte-grid-pagination">
          <button type="button" onClick={() => setPage(1)} disabled={currentPage === 1} title="Primera página">
            <i className="fas fa-angles-left" />
          </button>
          <button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1} title="Página anterior">
            <i className="fas fa-chevron-left" />
          </button>
          <strong>Página {currentPage} de {totalPages}</strong>
          <button type="button" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} title="Página siguiente">
            <i className="fas fa-chevron-right" />
          </button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} title="Última página">
            <i className="fas fa-angles-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataGrid;
